/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import { useForm } from "react-hook-form";

import { debounce } from "utils/debounce";
import * as Regex from 'utils/validations';
import { DataContext } from 'providers/data';
import { dataStates } from 'core/dataPerfilQuotation';
import { FormDataAddress } from 'utils/typeServices';

import { Typography, Box } from '@mui/material';
import Input from 'components/views/form/Input';
import Select from 'components/views/form/Select';
import { ButtonRectangle } from 'components/views/form/Button';

type ParamsValidateField = {
    currentValue: string;
    defaultValue: string;
    regex: RegExp;
}

const AddressForm = ({ pushProgressStep }: { pushProgressStep: () => void }) => {
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);
    const { dataSearchPersonData, dataByZipCode, handleGetByZipCode, handleUpdateFormAddress } = useContext(DataContext);
    const { register, setValue, handleSubmit, watch, reset } = useForm<FormDataAddress>();

    const [cep, street, complement, state, city, district, houseNumber] = watch(['cep', 'street', 'complement', 'state', 'city', 'district', 'houseNumber']);

    const handleOnSubmit = ({ cep, street, district, city, complement, type }: FormDataAddress) => {
        if (dataSearchPersonData && dataSearchPersonData.PESSOA) {
            const replaceCep = cep.replace('-', '');
            const { CD_PESSOA_ENDERECO, NM_ENDERECO, NM_BAIRRO, NR_CEP, DS_COMPLEMENTO, CD_TIPO_LOGRADOURO } = dataSearchPersonData.PESSOA.Endereco;

            handleUpdateFormAddress({
                CD_PESSOA_ENDERECO,
                CD_TIPO_LOGRADOURO: type || CD_TIPO_LOGRADOURO,
                DS_COMPLEMENTO: complement === DS_COMPLEMENTO ? null : complement,
                NM_BAIRRO: district === NM_BAIRRO ? null : street,
                NM_CIDADE: city,
                NM_ENDERECO: street === NM_ENDERECO ? null : street,
                CD_UF: state.value,
                NR_CEP: replaceCep === NR_CEP ? null : replaceCep,
                NR_NUMERO: parseFloat(houseNumber)
            });

            pushProgressStep();
        }
    }

    const debouncedConsultAddress = debounce((cep: string) => {
        const tokenExternal = localStorage.getItem('token_external');

        if (tokenExternal) {
            handleGetByZipCode({ cep: cep.replace('-', ''), token: tokenExternal });
            reset({
                city: '',
                district: '',
                street: '',
                state: { text: '', value: '' },
                houseNumber: '',
                complement: '',
            });
        }
    }, 500);

    const insertMaskCep = (cep: string) => {
        let cepAux = cep?.replace(/[^0-9]/g, '');
        cepAux = cepAux.substr(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
        setValue('cep', cepAux);

        return cepAux;
    }

    const validateField = ({ currentValue, defaultValue, regex }: ParamsValidateField) => {
        if (currentValue === defaultValue) return true;

        return !!currentValue && regex.test(currentValue);
    };

    useEffect(() => {
        if (dataSearchPersonData && dataSearchPersonData.PESSOA) {
            setValue('cep', `${dataSearchPersonData.PESSOA.Endereco.NR_CEP!.slice(0, -3)}-${dataSearchPersonData.PESSOA.Endereco.NR_CEP!.slice(-3)}`);
            setValue('city', dataSearchPersonData.PESSOA.Endereco.NM_CIDADE);
            setValue('district', dataSearchPersonData.PESSOA.Endereco.NM_BAIRRO!);
            setValue('street', dataSearchPersonData.PESSOA.Endereco.NM_ENDERECO!);
            setValue('state', { text: dataSearchPersonData.PESSOA.Endereco.CD_UF, value: dataSearchPersonData.PESSOA.Endereco.CD_UF });
            setValue('houseNumber', `${dataSearchPersonData.PESSOA.Endereco.NR_NUMERO}`);
            setValue('complement', dataSearchPersonData.PESSOA.Endereco.DS_COMPLEMENTO!);
        }

    }, [dataSearchPersonData]);

    useEffect(() => {
        if (cep && Regex.cep.test(cep)) {
            if (dataByZipCode && (dataByZipCode?.cep === cep.replace('-', ''))) {
                return;
            }

            debouncedConsultAddress(cep);
        }
        return () => debouncedConsultAddress.cancel();
    }, [cep]);

    useEffect(() => {
        if (dataByZipCode) {
            setValue('city', dataByZipCode?.cityName);
            setValue('district', dataByZipCode?.district);
            setValue('street', dataByZipCode?.fullAddress);
            setValue('state', { text: dataByZipCode?.stateName, value: dataByZipCode?.stateName });
            setValue('type', dataByZipCode?.type);
        }

    }, [dataByZipCode]);

    useEffect(() => {
        if (dataSearchPersonData && dataSearchPersonData.PESSOA) {
            const { NR_CEP, NM_CIDADE, NM_BAIRRO, CD_UF, NR_NUMERO, NM_ENDERECO, } = dataSearchPersonData.PESSOA.Endereco;

            const isValid =
                validateField({ currentValue: cep, defaultValue: `${NR_CEP!.slice(0, -3)}-${NR_CEP!.slice(-3)}`, regex: Regex.cep }) &&
                validateField({ currentValue: city, defaultValue: NM_CIDADE, regex: Regex.city }) &&
                validateField({ currentValue: district, defaultValue: NM_BAIRRO!, regex: Regex.district }) &&
                validateField({ currentValue: street, defaultValue: NM_ENDERECO!, regex: Regex.address }) &&
                (!state.value || state.value === CD_UF || !!state.value) &&
                validateField({ currentValue: houseNumber, defaultValue: `${NR_NUMERO}`, regex: Regex.hsNumber })

            if (isValid) {
                setIsBtnDisabled(false);
            } else {
                setIsBtnDisabled(true);
            }
        }

    }, [cep, street, complement, state, city, district, houseNumber, dataSearchPersonData]);

    return (
        <Box component='form' sx={{
            'button': {
                width: '100%',
                marginTop: {
                    xs: '1.25rem',
                    md: '1rem'
                }
            }
        }} onSubmit={handleSubmit(handleOnSubmit)}>
            <Box sx={{
                borderRadius: '10px',
                padding: { md: '1.38rem 1.2rem 1.53rem' },
                border: {
                    xs: 'none',
                    md: '1.5px solid rgba(12, 28, 51, 0.5)'
                }
            }}>
                <Typography fontSize='1rem' fontWeight={700} paddingBottom='1.25rem'>
                    Endereço de Cobrança
                </Typography>

                <Box sx={{ width: { xs: 'calc(50% - .655rem)', md: '9.85rem' } }}>
                    <Input
                        name='cep'
                        label='CEP'
                        placeholder={dataSearchPersonData && dataSearchPersonData.PESSOA ? dataSearchPersonData.PESSOA.Endereco.NR_CEP! : '99999-999'}
                        type='text'
                        register={{
                            ...register("cep", {
                                onChange(event) {
                                    event.target.value = insertMaskCep(event.target.value);
                                }
                            })
                        }}
                    />
                </Box>

                <Input
                    name="street"
                    label="Endereço cadastrado"
                    placeholder="Nome"
                    type="text"
                    register={{ ...register("street") }}
                />

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: '9.8rem 14rem' },
                    columnGap: { xs: '1.31rem', md: '.88rem' }
                }}>
                    <Input
                        name='houseNumber'
                        label='Número'
                        placeholder='0000'
                        maskHolder='99999'
                        mask
                        type='tel'
                        register={{ ...register('houseNumber') }}
                    />

                    <Input
                        name="complement"
                        label="Complemento"
                        placeholder="Complemento"
                        type="text"
                        register={{ ...register("complement") }}
                    />

                    <Input
                        name="district"
                        label="Bairro"
                        placeholder="Bairro"
                        type="text"
                        register={{ ...register("district") }}
                    />

                    <Input
                        name="city"
                        label="Cidade"
                        placeholder="Cidade"
                        type="text"
                        register={{ ...register("city") }}
                    />
                </Box>

                <Box sx={{ width: { xs: 'calc(50% - .655rem)', md: '9.85rem' } }}>
                    <Select
                        label='Estado'
                        itemSelected={state}
                        placeholder='Selecione'
                        listItems={dataStates}
                        onChangeSelected={(e) => setValue("state", e, { shouldValidate: true })}
                    />
                </Box>
            </Box>

            <ButtonRectangle name='CONTINUAR' type='submit' disabled={isBtnDisabled} />
        </Box>
    );
}

export default AddressForm;